<template>
  <div
    v-if="isVisible"
    :class="['toast', status, position]"
    :style="toastStyle"
    ref="toastRef"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
  >
    <div class="toast__status">
      <svg v-if="status === 'success'" class="toast__status-icon">
        <use xlink:href="#check-mark-icon-success"></use>
      </svg>
    </div>

    <main :class="['toast__content', { elipsis: enableElipsis }]">
      <h4 v-if="title" :class="['toast__content-heading', { elipsis: enableElipsis }]">{{ title }}</h4>
      <p v-if="message || templateType !== 'default'" :class="['toast__content-message', { elipsis: enableElipsis }]">
        <slot v-if="templateType == 'wishlist:sign-in'" name="wishlist:sign-in" />
        <slot v-if="templateType == 'wishlist-page:sign-in'" name="wishlist-page:sign-in" />
        <span v-else-if="message" :class="[customMessageClass]">{{ message }}</span>
      </p>
    </main>

    <button @click="closeToast" aria-label="Close toast" class="toast__close-cta">
      <svg class="toast__close-icon">
        <use xlink:href="#icon-cross"></use>
      </svg>
    </button>
  </div>
</template>

<script setup>
  import { onBeforeUnmount, onMounted, ref, nextTick } from 'vue';

  const toastRef = ref(null);
  const isVisible = ref(false);
  const status = ref('success');
  const title = ref('');
  const message = ref('');
  const position = ref('center');
  const templateType = ref('default');
  const customMessageClass = ref('');
  const enableElipsis = ref(false);
  const toastStyle = ref({});
  const currentCursorPosition = ref({});
  let timerId;

  onMounted(() => {
    window.addEventListener('showToast', showToast);
    window.addEventListener('closeToast', closeToast);
    window.addEventListener('mousemove', updateToastPosition);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('showToast', showToast);
    window.removeEventListener('closeToast', closeToast);
    window.removeEventListener('mousemove', updateToastPosition);
  });

  const showToast = async (event) => {
    const { detail } = event;

    status.value = detail.status || 'success';
    title.value = detail.title || '';
    message.value = detail.message || '';
    templateType.value = detail.templateType || 'default';
    position.value = detail.position || 'center';
    customMessageClass.value = detail.customMessageClass || '';
    enableElipsis.value = detail.enableElipsis || false;

    isVisible.value = true;
    await nextTick();

    // Determine if it should show at cursor position
    if (detail.showInCurrentCursorPosition) {
      const toastWidth = toastRef.value.offsetWidth;

      toastStyle.value = {
        top: `${currentCursorPosition.value.clientY}px`,
        left: `${currentCursorPosition.value.clientX - toastWidth}px`, // Adjust left position
        transform: 'none',
      };
    }

    timerId = setTimeout(closeToast, detail.duration || 2000);
  };

  const closeToast = () => {
    isVisible.value = false;
    clearTimeout(timerId);
  };

  const updateToastPosition = (event) => {
    currentCursorPosition.value.clientX = event.clientX;
    currentCursorPosition.value.clientY = event.clientY;
  };
</script>
